import { useMutation } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import { SectionPanel } from '../../shared/ui/section-panel';
import { sendPasswordResetEmail } from '../../entities/user';
import type { TUser } from '../../entities/session';
import type { TAccountProfile } from './use-account-profile';

export const AccountProfile = ({ user, profile }: { user: TUser; profile: TAccountProfile }) => {
  const reset = useMutation({ mutationFn: () => sendPasswordResetEmail(user.email) });

  return (
    <SectionPanel title="You" note="the same in every organization you belong to">
      <div className="grid grid-cols-2 gap-5 px-card py-4">
        <div>
          <FieldLabel htmlFor="account-name">Name</FieldLabel>
          <Input
            id="account-name"
            value={profile.name}
            invalid={!profile.nameOk}
            onChange={(event) => profile.change(event.target.value)}
          />
          <Hint>What people in your organizations see next to your changes.</Hint>
        </div>
        <div>
          <FieldLabel htmlFor="account-email">Email</FieldLabel>
          <Input id="account-email" mono value={user.email} readOnly disabled />
          <Hint>You sign in with it. It can’t be changed here.</Hint>
        </div>
      </div>
      {profile.save.isError && (
        <div className="px-card pb-4">
          <MutationError error={profile.save.error} />
        </div>
      )}

      <div className="flex items-center gap-6 border-t border-border-sunken px-card py-4">
        <div className="min-w-0 flex-1">
          <p className="text-data font-medium text-primary">Password</p>
          <p className="mt-1 text-meta leading-18 text-muted">
            We email you a link to set a new one. It works for an hour, and using it signs you out
            everywhere else.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          waiting={reset.isPending}
          waitingLabel="Sending"
          onClick={() => reset.mutate()}
        >
          Email me a reset link
        </Button>
      </div>

      {(reset.isSuccess || reset.isError) && (
        <div className="px-card pb-4">
          {reset.isSuccess && <Note>A link is on its way to {user.email}.</Note>}
          {reset.isError && (
            <Note tone="danger">The link wasn’t sent. Your password hasn’t changed.</Note>
          )}
        </div>
      )}
    </SectionPanel>
  );
};
